import React, { useEffect, useState, useRef, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import { useWizard } from 'react-use-wizard'
import { uniq } from 'lodash'
import { Tree } from 'react-arborist'
import { useDispatch, useSelector } from 'react-redux'
import { Button, PageTitle } from 'web/src/app/root/components/common'
import { getLocationsTree } from 'web/src/app/locations/selectors'
import { locationsRequest } from 'web/src/app/locations/actions'
import {
    treeNodeChildrenAccessor,
    treeNodeIdAccessor,
    useTreeUtils
} from 'web/src/app/root/components/common/locations-tree/tree-utils'
import { TreeNode } from 'web/src/app/root/components/common/locations-tree/tree-node'
import { useCreateUser } from '../../context/create-user-context'
import { needSkipLocationsAssign } from '../../utils'
import Input from 'web/src/app/root/components/common/input'

export const NewUserLocations = () => {
    const styles = useStyles()
    const { userState, onUserUpdate, isFromLocationAssignment } =
        useCreateUser()
    const locations = useSelector(state => state.locations.locations)

    const { locale } = useSelector(state => state.main)
    const dispatch = useDispatch()
    const rootElement = useRef(null)
    const treeRef = useRef(null)

    const [selected, setSelected] = useState(userState.locations || [])
    const [locationsTreeNoCustomerName, setLocationsTreeNoCustomerName] =
        useState({})
    const [locationsIds, setLocationsIds] = useState([])
    const [locationSearchStr, setLocationSearchStr] = useState('')

    const isAllSelected = useMemo(
        () =>
            selected &&
            selected.length &&
            locationsIds.length === selected.length,
        [selected]
    )

    const { previousStep, nextStep } = useWizard()

    const treeUtils = useTreeUtils(locationsTreeNoCustomerName)

    const handleNextStep = () => {
        onUserUpdate({ locations: selected })
        nextStep()
    }

    const selectAll = () => {
        if (isAllSelected) {
            return setSelected([])
        }
        setSelected(locationsIds)
    }

    const handleSelect = (checked, location) => {
        let newSelected = []
        const { id, owner_id } = location

        if (checked) {
            // let idsToSelect = [id]

            // let parentId = parent_id;

            // while (parentId) {
            //     const node = treeUtils.find(treeUtils.root, parentId)
            //     idsToSelect.push(node.model.id)
            //     parentId = node.model.location.parent_id;
            // }

            newSelected = [...selected, { id, original_owner_id: owner_id }]
        } else {
            newSelected = [...selected].filter(i => i.id !== id)
        }
        setSelected(uniq(newSelected))
    }

    const handleLocationSearch = searchStr => {
        setLocationSearchStr(searchStr)
    }

    useEffect(() => {
        dispatch(locationsRequest({ is_original: true }))
    }, [])

    useEffect(() => {
        // skip locations select step if custom role has no locations access
        if (
            needSkipLocationsAssign(
                userState.role_code,
                userState.role,
                isFromLocationAssignment
            )
        ) {
            nextStep()
        }
    }, [locationsIds, userState.role])

    useEffect(() => {
        if (locations) {
            let locationsFiltered = [...locations]
            if (locationSearchStr.trim().length > 0) {
                locationsFiltered = locationsFiltered.filter(l =>
                    l.name
                        .toLowerCase()
                        .includes(locationSearchStr.toLowerCase())
                )
            }
            const { locationsTreeNoCustomerName: lTree, locationsIds: lIds } =
                getLocationsTree(locationsFiltered)
            setLocationsTreeNoCustomerName(lTree)
            setLocationsIds(lIds)
        }
    }, [locations, locationSearchStr])

    return (
        <div className={styles.root}>
            <PageTitle
                position={'center'}
                title={locale.userManagement.selectLocationsStepTitle}
            />
            <div className={styles.contentWrapper}>
                <div className={styles.treeWrapper} ref={rootElement}>
                    <div className={styles.selectAllWrapper}>
                        <div></div>
                        <Button reverted onClick={selectAll}>
                            {isAllSelected
                                ? locale.userManagement.deselectAllLocations
                                : locale.userManagement.selectAllLocations}
                        </Button>
                    </div>
                    <div>
                        <Input
                            label={locale.common.search}
                            type="text"
                            setValue={handleLocationSearch}
                            debounce={600}
                            value={locationSearchStr}
                            size="small"
                            className={styles.locatonsSearchInput}
                        />
                    </div>
                    {treeUtils.data ? (
                        <Tree
                            ref={treeRef}
                            indent={24}
                            rowHeight={37}
                            width={600}
                            rowClassName={styles.treeRow}
                            data={treeUtils.data?.children || []}
                            idAccessor={treeNodeIdAccessor}
                            childrenAccessor={treeNodeChildrenAccessor}
                            openByDefault
                            disableSelect>
                            {props => (
                                <TreeNode
                                    locationSearchStr={locationSearchStr}
                                    isUserMgmt
                                    isSelected={selected
                                        .map(l => l.id)
                                        .includes(props.node.data.id)}
                                    onSelect={handleSelect}
                                    {...props}
                                />
                            )}
                        </Tree>
                    ) : null}
                </div>
            </div>
            <div className={styles.buttonsBlock}>
                <Button reverted onClick={previousStep}>
                    {locale.userManagement.stepBack}
                </Button>
                <Button onClick={handleNextStep}>
                    {locale.userManagement.nextStep}
                </Button>
            </div>
        </div>
    )
}

const useStyles = makeStyles(theme => ({
    selectAllWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 8
    },
    devicesWrapper: { flex: 1, marginLeft: '32px' },
    treeWrapper: { height: '100%', width: 600 },
    treeRow: {
        minWidth: '0 !important'
    },
    contentWrapper: {
        display: 'flex',
        width: '100%',
        flex: 1,
        justifyContent: 'center'
    },
    devices: {
        height: 'calc(100% - 75px)'
    },
    buttonsBlock: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 20,
        marginBottom: 46,
        '&& > :last-child': {
            marginLeft: 16
        }
    },
    root: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        flex: 1,
        padding: '40px 94px 0'
    }
}))
